import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Save, Search, Check } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = {
  'Header & Navigation': ['rooms', 'howItWorks', 'locations', 'contact', 'bookNow'],
  'Hero Section': ['heroTagline', 'canYou', 'escape', 'heroDescription', 'exploreRooms'],
  'Flashcard Flip Words': ['flipWord1', 'flipWord2', 'flipWord3', 'flipWord4', 'flipWord5', 'flipWord6'],
  'Feature Widgets': ['privateRooms', 'justYourGroup', 'sixtyMinutes', 'raceClock', 'immersivePuzzles', 'storyClues', 'findClues', 'searchCorner', 'unlockEscape', 'earnFreedom', 'navigate', 'chartCourse'],
  'Escape Rooms': ['ourEscapeRooms', 'escapeRoomsLabel', 'chooseAdventure', 'bookThisRoom', 'bankHeist', 'survivalEscape', 'treasureHunt', 'sweetEscape', 'oceans13Desc', 'demolitionDesc', 'pirateDesc', 'sweetEscapeDesc', 'upTo', 'from', 'min'],
  'How It Works': ['playing', 'okiEscape', 'howItWorksDesc', 'everyonePlays', 'everyoneDesc', 'everyoneAccent', 'everyoneList', 'teamBuilding', 'teamDesc', 'teamAccent', 'returnGames', 'returnDesc', 'returnAccent'],
  'Locations & Contact': ['findUs', 'us', 'locationsDesc', 'bankHeistSurvival', 'pirateShipExperience', 'viewOnMaps', 'getInTouch', 'touch', 'confused'],
  'Pricing': ['pricing', 'pricingPerPerson', 'adult', 'kids', 'toddlers', 'free', 'pricingNote', 'pricingDiscount'],
  'Crossword Puzzle': ['solveThe', 'puzzle', 'crosswordDesc', 'across', 'down', 'clueEscape', 'clueI', 'clueCan', 'iCanEscape', 'crosswordSolved', 'messageOnFacebook'],
  'Key Lock Puzzle': ['unlockThe', 'lock', 'dragKey', 'unlocked', 'unlockedMsg'],
  'Popup Slogans': ['slogan1', 'slogan2', 'slogan3', 'slogan4', 'slogan5', 'slogan6', 'slogan7', 'slogan8'],
  'CTA & Booking': ['readyToTest', 'ctaDesc', 'chooseLocation', 'rycomRooms', 'gateRooms'],
  'Widgets & Footer': ['dontKnow', 'needHelp', 'messageUsNow', 'social', 'weeklyPuzzle', 'footerTagline', 'on', 'off'],
  'Weekly Puzzle Page': ['puzzleLabel', 'puzzleHeading', 'puzzleSubheading', 'puzzleNoneTitle', 'puzzleNoneDesc', 'puzzleSubmitTitle', 'puzzleSubmitBtn', 'puzzleCorrect', 'puzzleWrong', 'puzzleCorrectDesc', 'puzzleWrongDesc', 'puzzleFacebookBtn', 'puzzleRetryBtn'],
};

export default function ContentEditor() {
  const { fetchContent } = useLanguage();
  const [content, setContent] = useState({});
  const [edited, setEdited] = useState({});
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/content`).then(res => {
      const map = {};
      res.data.forEach(item => { map[item.key] = { en: item.en, ja: item.ja }; });
      setContent(map);
      setEdited(JSON.parse(JSON.stringify(map)));
    });
  }, []);

  const handleChange = (key, lang, value) => {
    setEdited(prev => ({ ...prev, [key]: { ...prev[key], [lang]: value } }));
    setSaved(false);
  };

  const getChangedKeys = () => Object.keys(edited).filter(key => content[key] && (edited[key].en !== content[key].en || edited[key].ja !== content[key].ja));

  const handleSave = async () => {
    setSaving(true);
    const updates = getChangedKeys().map(key => ({ key, en: edited[key].en, ja: edited[key].ja }));
    try {
      await axios.put(`${API}/api/content`, { updates }, { withCredentials: true });
      setContent(JSON.parse(JSON.stringify(edited)));
      setSaved(true);
      fetchContent();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const filteredSections = Object.entries(SECTIONS).map(([section, keys]) => {
    const filtered = keys.filter(key => {
      if (!search) return true;
      const s = search.toLowerCase();
      return key.toLowerCase().includes(s) || (edited[key]?.en || '').toLowerCase().includes(s) || (edited[key]?.ja || '').toLowerCase().includes(s);
    });
    return [section, filtered];
  }).filter(([, keys]) => keys.length > 0);

  return (
    <div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input data-testid="admin-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search translations..." className="w-full bg-neutral-900 border border-neutral-800 rounded-xl pl-10 pr-4 py-3 text-white font-sans text-sm focus:border-brand focus:outline-none" />
        </div>
        <button onClick={handleSave} disabled={saving || getChangedKeys().length === 0} data-testid="save-content-btn" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-3 rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-30">
          {saved ? <Check size={14} /> : <Save size={14} />}
          {saving ? 'Saving...' : saved ? 'Saved!' : `Save (${getChangedKeys().length})`}
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-24 space-y-2">
        {filteredSections.map(([section, keys]) => (
          <div key={section} className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
            <button onClick={() => setOpenSection(openSection === section ? null : section)} data-testid={`section-${section.replace(/\s+/g, '-').toLowerCase()}`} className="w-full flex items-center justify-between px-5 py-4 hover:bg-neutral-800 transition-colors">
              <span className="font-heading text-base tracking-wide uppercase text-white">{section}</span>
              <div className="flex items-center gap-2">
                <span className="font-sans text-xs text-neutral-500">{keys.length} items</span>
                <span className={`text-neutral-500 transition-transform ${openSection === section ? 'rotate-180' : ''}`}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg></span>
              </div>
            </button>
            {openSection === section && (
              <div className="border-t border-neutral-800 divide-y divide-neutral-800">
                {keys.map(key => {
                  const isChanged = content[key] && (edited[key]?.en !== content[key]?.en || edited[key]?.ja !== content[key]?.ja);
                  return (
                    <div key={key} className={`px-5 py-4 ${isChanged ? 'bg-brand/5' : ''}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="font-mono text-xs text-brand bg-brand/10 px-2 py-0.5 rounded">{key}</span>
                        {isChanged && <span className="text-[10px] font-sans text-brand font-bold uppercase">Modified</span>}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <div>
                          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">English</label>
                          {(edited[key]?.en || '').length > 80 ? (
                            <textarea data-testid={`edit-en-${key}`} value={edited[key]?.en || ''} onChange={e => handleChange(key, 'en', e.target.value)} rows={3} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 text-white font-sans text-sm focus:border-brand focus:outline-none resize-y" />
                          ) : (
                            <input data-testid={`edit-en-${key}`} type="text" value={edited[key]?.en || ''} onChange={e => handleChange(key, 'en', e.target.value)} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 text-white font-sans text-sm focus:border-brand focus:outline-none" />
                          )}
                        </div>
                        <div>
                          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Japanese</label>
                          {(edited[key]?.ja || '').length > 80 ? (
                            <textarea data-testid={`edit-ja-${key}`} value={edited[key]?.ja || ''} onChange={e => handleChange(key, 'ja', e.target.value)} rows={3} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 text-white font-sans text-sm focus:border-brand focus:outline-none resize-y" />
                          ) : (
                            <input data-testid={`edit-ja-${key}`} type="text" value={edited[key]?.ja || ''} onChange={e => handleChange(key, 'ja', e.target.value)} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 text-white font-sans text-sm focus:border-brand focus:outline-none" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Staff Reminders Editor */}
      <RemindersEditor />
    </div>
  );
}

function RemindersEditor() {
  const [reminders, setReminders] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/reminders`, { withCredentials: true })
      .then(res => { setReminders(res.data.messages || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const save = async (list) => {
    setSaving(true);
    await axios.put(`${API}/api/admin/reminders`, { messages: list }, { withCredentials: true });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    const next = [...reminders, v];
    setReminders(next);
    setDraft('');
    save(next);
  };

  const remove = (i) => {
    const next = reminders.filter((_, j) => j !== i);
    setReminders(next);
    save(next);
  };

  const update = (i, val) => {
    const next = reminders.map((r, j) => j === i ? val : r);
    setReminders(next);
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden mt-4">
      <button
        onClick={() => setOpen(!open)}
        data-testid="reminders-section-toggle"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-heading text-sm tracking-widest uppercase text-foreground">Staff Reminders</span>
          <span className="text-[10px] font-sans text-muted-foreground">{reminders.length} items</span>
        </div>
        <span className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          <p className="font-sans text-xs text-muted-foreground mb-2">These pop up as reminders for staff in the admin panel. Add, edit, or remove them.</p>
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading...</p>
          ) : (
            <>
              {reminders.map((r, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`reminder-edit-${i}`}>
                  <input
                    value={r}
                    onChange={e => update(i, e.target.value)}
                    onBlur={() => save(reminders)}
                    className="flex-1 bg-secondary/30 border border-border rounded-lg px-3 py-2 font-sans text-sm text-foreground focus:border-brand focus:outline-none"
                  />
                  <button
                    onClick={() => remove(i)}
                    className="text-muted-foreground hover:text-red-400 transition-colors p-1"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && add()}
                  placeholder="Add new reminder..."
                  data-testid="reminder-add-input"
                  className="flex-1 bg-secondary/30 border border-border rounded-lg px-3 py-2 font-sans text-sm text-foreground placeholder-muted-foreground focus:border-brand focus:outline-none"
                />
                <button onClick={add} disabled={!draft.trim()} data-testid="reminder-add-btn" className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-2 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-30">
                  + Add
                </button>
              </div>
              {saved && <p className="font-sans text-xs text-green-500 flex items-center gap-1"><Check size={12} /> Saved</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
